import { useMemo } from 'react';
import { useBetslipContext } from '@/features/betslip/contexts/betslip-context';
import { useSiteStore } from '@/store/site';
import { useBetslipStore } from '@/store/betslip';
import { useAccountInfo } from '@/hooks/query/account';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';
import { useWcPromotionSetting } from './use-wc-promotion-setting';

export const TICKET_STATES = {
	NO_STAKE: 'NO_STAKE',
	AWAY_COUNT: 'AWAY_COUNT',
	REACHED: 'REACHED',
	NO_AMOUNT: 'NO_AMOUNT',
} as const;

export interface WcPromotionTicketEligibilityInfo {
	isEligible: boolean;
	targetAmount: number;
	currentStake: number;
	awayCount: number;
	ticketState: (typeof TICKET_STATES)[keyof typeof TICKET_STATES];
	currencyName: string;
}

const getTicketState = (
	currentStake: number,
	awayCount: number
): (typeof TICKET_STATES)[keyof typeof TICKET_STATES] => {
	if (!currentStake) return TICKET_STATES.NO_STAKE;
	if (awayCount > 0) return TICKET_STATES.AWAY_COUNT;
	if (awayCount === 0) return TICKET_STATES.REACHED;
	return TICKET_STATES.NO_AMOUNT;
};

export const useWcPromotionTicketEligibility = ({
	SportType,
	LeagueGroupId,
	stake,
}: {
	SportType: number;
	LeagueGroupId: number;
	stake: number;
}): WcPromotionTicketEligibilityInfo => {
	const { data: wcSetting } = useWcPromotionSetting();
	const {  enableWcPromotionWeeklyLottery, enableWcPromotionAfterParty } =
		useLicSettings();
	const isWcPromotionEnabled = enableWcPromotionWeeklyLottery || enableWcPromotionAfterParty;

	const { user } = useAccountInfo();
	const { currencyName } = useBetslipContext();
	const [isParlayMode] = useSiteStore(state => [state.isParlayMode]);
	const [betslipTab, useSabaCoin] = useBetslipStore(state => [state.betslipTab, state.useSabaCoin]);
	const isParlayTab = betslipTab === 'parlay';

	const isEligibleLeagueAndGroup = useMemo(() => {
		return Boolean(
			isWcPromotionEnabled &&
				wcSetting?.SportId === SportType &&
				wcSetting.LeagueGroupId?.includes(LeagueGroupId)
		);
	}, [isWcPromotionEnabled, wcSetting, SportType, LeagueGroupId]);

	const currencySetting = useMemo(() => {
		if (!wcSetting || !user || !isEligibleLeagueAndGroup) return null;

		const userCurrencyId = user.CurrId;
		return wcSetting.CurrencySettings.find(setting => setting.CurrencyId === userCurrencyId);
	}, [wcSetting, user, isEligibleLeagueAndGroup]);

	const targetAmount = useMemo(() => currencySetting?.TargetAmount ?? 0, [currencySetting]);

	const awayCount = useMemo(() => {
		if (!currencySetting) return 0;

		const currentStake = stake || 0;
		const shortfall = Math.max(0, targetAmount - currentStake);
		return Math.ceil(shortfall);
	}, [currencySetting, targetAmount, stake]);

	const isEligible = useMemo(() => {
		if (!isEligibleLeagueAndGroup || isParlayMode || isParlayTab || useSabaCoin) return false;
		return true;
	}, [isEligibleLeagueAndGroup, isParlayMode, isParlayTab, useSabaCoin]);

	const ticketState = getTicketState(stake, awayCount);

	return {
		isEligible,
		targetAmount,
		currentStake: stake || 0,
		awayCount,
		currencyName,
		ticketState,
	};
};
